import { Download, Share, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";
import { usePWAInstall } from "@/lib/pwa-install";

/**
 * Floating banner that nudges the user to install the PWA. Same shape
 * as `<PWAUpdatePrompt>` so the two read as siblings.
 *
 * Two variants depending on platform:
 *
 *   - **Chromium** (Chrome / Edge / Android): "Install" button calls
 *     the captured `beforeinstallprompt` event. Browser handles the
 *     rest of the flow (system dialog).
 *   - **iOS Safari**: no native install API. The banner is purely
 *     instructional — "Tap [Share] then 'Add to Home Screen'" — with
 *     just a dismiss ✕. The user has to do the rest manually.
 *
 * Cooldown after dismissal is incremental (7d → 10d → 30d, capped) so
 * a not-now-thanks doesn't either spam or permanently silence. See
 * `usePWAInstall` for the orchestration.
 */
export function PWAInstallPrompt() {
  const { platform, shouldShowBanner, install, dismiss } = usePWAInstall();
  const t = useT();

  if (!shouldShowBanner) return null;

  const isIOS = platform === "ios-safari";

  return (
    <div
      className="fixed inset-x-0 z-50 px-4"
      style={{
        // Sit just above the bottom nav (72px) + iOS safe-area inset.
        // Identical math to PWAUpdatePrompt so the two never overlap
        // visually — only one is meaningful at a time anyway (you
        // can't have an unbuilt update before you're installed).
        bottom: "calc(72px + env(safe-area-inset-bottom) + 12px)",
      }}
      role="status"
      aria-live="polite"
    >
      <div className="mx-auto flex max-w-[480px] items-center gap-3 rounded-[var(--r-lg)] border border-border bg-surface px-4 py-3 shadow-[var(--shadow-md)]">
        <div
          className="flex size-9 shrink-0 items-center justify-center rounded-full"
          style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
        >
          <Download className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-semibold">{isIOS ? t("pwa.install_ios_title") : t("pwa.install_title")}</div>
          <div className="mt-0.5 flex items-center gap-1 text-[12px] text-text-3">
            {isIOS ? (
              <>
                {/* Renders as: "Tap [share-icon] then 'Add to Home Screen'".
                    The inline icon makes the instruction unambiguous on
                    devices where the Share button position varies (Safari
                    moved it between the URL bar and bottom toolbar across
                    iOS versions). */}
                {t("pwa.install_ios_hint_before")}
                <Share className="inline size-3.5" aria-hidden="true" />
                {t("pwa.install_ios_hint_after")}
              </>
            ) : (
              t("pwa.install_hint")
            )}
          </div>
        </div>
        {!isIOS && (
          <Button size="sm" onClick={install}>
            {t("pwa.install_action")}
          </Button>
        )}
        <button
          type="button"
          aria-label={t("pwa.dismiss")}
          onClick={dismiss}
          className="flex size-8 shrink-0 items-center justify-center rounded-full text-text-3 transition-colors hover:bg-[color:var(--surface-2)]"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}
